/**
 * Zustand store for Marketing Copilot state.
 */

import { create } from 'zustand'
import type {
  CopilotMessage,
  CopilotConversation,
  CopilotPersona,
  Visualization,
  ActionProposal,
  SessionUsage,
  BudgetStatus,
} from '@/types/copilot'

interface CopilotState {
  // Panel
  isOpen: boolean
  layout: 'split' | 'overlay' | 'fullscreen'

  // Conversation
  conversations: CopilotConversation[]
  conversationId: string | null
  messages: CopilotMessage[]
  isStreaming: boolean
  streamingMessageId: string | null

  // Visualizations (AI-controlled, currently active)
  activeVisualizations: Visualization[]

  // Actions
  pendingActions: ActionProposal[]

  // Voice
  isVoiceActive: boolean
  // Persona (configured identity; null until loaded / when unconfigured)
  persona: CopilotPersona | null

  // Language
  language: 'en' | 'es'

  // Briefing
  briefing: string | null
  briefingAnomalies: Array<{ campaign: string; metric: string; direction: string; pct_change: number; severity: string; sentiment: string }>

  // Usage tracking
  sessionUsage: SessionUsage
  budgetStatus: BudgetStatus | null

  // Setters
  setOpen: (open: boolean) => void
  toggleOpen: () => void
  setLayout: (layout: 'split' | 'overlay' | 'fullscreen') => void
  setConversations: (conversations: CopilotConversation[]) => void
  setConversationId: (id: string | null) => void
  setMessages: (messages: CopilotMessage[]) => void
  addMessage: (message: CopilotMessage) => void
  appendToMessage: (messageId: string, content: string) => void
  updateMessage: (messageId: string, updates: Partial<CopilotMessage>) => void
  setStreaming: (streaming: boolean, messageId?: string | null) => void
  addVisualization: (viz: Visualization) => void
  clearVisualizations: () => void
  addActionProposal: (action: ActionProposal) => void
  removeActionProposal: (actionId: string) => void
  setVoiceActive: (active: boolean) => void
  setPersona: (persona: CopilotPersona | null) => void
  setLanguage: (lang: 'en' | 'es') => void
  setBriefing: (briefing: string | null, anomalies?: Array<{ campaign: string; metric: string; direction: string; pct_change: number; severity: string; sentiment: string }>) => void
  addUsage: (tokens: number, promptTokens: number, completionTokens: number, costUsd: number) => void
  setBudgetStatus: (status: BudgetStatus | null) => void
  newConversation: () => void
  reset: () => void
}

const initialSessionUsage: SessionUsage = {
  totalTokens: 0,
  promptTokens: 0,
  completionTokens: 0,
  totalCostUsd: 0,
  requestCount: 0,
}

const initialState = {
  isOpen: false,
  layout: 'split' as const,
  conversations: [],
  conversationId: null,
  messages: [],
  isStreaming: false,
  streamingMessageId: null,
  activeVisualizations: [],
  pendingActions: [],
  isVoiceActive: false,
  persona: null,
  language: 'en' as const,
  briefing: null,
  briefingAnomalies: [],
  sessionUsage: { ...initialSessionUsage },
  budgetStatus: null,
}

export const useCopilotStore = create<CopilotState>()((set) => ({
  ...initialState,

  setOpen: (open) => set({ isOpen: open }),
  toggleOpen: () => set((s) => ({ isOpen: !s.isOpen })),
  setLayout: (layout) => set({ layout }),
  setConversations: (conversations) => set({ conversations }),
  setConversationId: (id) => set({ conversationId: id }),
  setMessages: (messages) => set({ messages }),

  addMessage: (message) => set((s) => ({
    messages: [...s.messages, message],
  })),

  appendToMessage: (messageId, content) => set((s) => ({
    messages: s.messages.map((m) =>
      m.id === messageId ? { ...m, content: m.content + content } : m
    ),
  })),

  updateMessage: (messageId, updates) => set((s) => ({
    messages: s.messages.map((m) =>
      m.id === messageId ? { ...m, ...updates } : m
    ),
  })),

  setStreaming: (streaming, messageId = null) => set({
    isStreaming: streaming,
    streamingMessageId: messageId,
  }),

  addVisualization: (viz) => set((s) => ({
    activeVisualizations: [...s.activeVisualizations, viz],
  })),

  clearVisualizations: () => set({ activeVisualizations: [] }),

  addActionProposal: (action) => set((s) => ({
    pendingActions: [...s.pendingActions, action],
  })),

  removeActionProposal: (actionId) => set((s) => ({
    pendingActions: s.pendingActions.filter((a) => a.id !== actionId),
  })),

  setVoiceActive: (active) => set({ isVoiceActive: active }),
  setPersona: (persona) => set({ persona }),
  setLanguage: (lang) => set({ language: lang }),
  setBriefing: (briefing, anomalies) => set({ briefing, briefingAnomalies: anomalies || [] }),

  addUsage: (tokens, promptTokens, completionTokens, costUsd) => set((s) => ({
    sessionUsage: {
      totalTokens: s.sessionUsage.totalTokens + tokens,
      promptTokens: s.sessionUsage.promptTokens + promptTokens,
      completionTokens: s.sessionUsage.completionTokens + completionTokens,
      totalCostUsd: s.sessionUsage.totalCostUsd + costUsd,
      requestCount: s.sessionUsage.requestCount + 1,
    },
  })),

  setBudgetStatus: (status) => set({ budgetStatus: status }),

  newConversation: () => set({
    conversationId: null,
    messages: [],
    activeVisualizations: [],
    pendingActions: [],
    streamingMessageId: null,
    isStreaming: false,
  }),

  reset: () => set(initialState),
}))
